<template>
  <div>
    <v-app-bar app flat clipped-right dark color="primary">
      <v-app-bar-nav-icon
        @click.native.stop="updateUILeftDrawerState(!UILeftDrawer)"
        v-if="!screenIsWideEnough"
      ></v-app-bar-nav-icon>
      <v-layout>
        <v-flex xs9 lg6 xl11>
          <v-text-field
            name="search"
            v-model="search"
            prepend-inner-icon="search"
            clearable
            flat
            label="Search Referrals"
            solo-inverted
            hide-details
            autocomplete="off"
          ></v-text-field>
        </v-flex>
        <v-spacer></v-spacer>
        <v-btn icon @click.native.stop="showFilterDrawer = true" v-if="!screenIsWideEnough">
          <v-icon>filter_list</v-icon>
        </v-btn>
        <ReferralKey />
        <span v-if="!UserHasRole('Call Centre') && !UserHasRole('Platform')">
          <span v-if="ReferralDownloadingState">
            <v-progress-circular :indeterminate="true" :size="24" color="pink"></v-progress-circular> BUILDING CSV FILE
          </span>
          <v-btn v-else text @click="download">
            <v-icon class="mr-1">fas fa-file-excel</v-icon> Download Referrals
          </v-btn>
        </span>
      </v-layout>
      <v-spacer></v-spacer>
    </v-app-bar>
    <v-content>
      <v-container fluid py-0 class="grey lighten-2">
        <v-layout class="fix" column pr-4 v-if="isFiltered" transition="slide-y-transition">
          <abf-active-filters @change="page = 1" @reset="resetReferralFilters"></abf-active-filters>
        </v-layout>
        <v-layout
          class="calc-height"
          :class="{ isFiltered: 'filtered' }"
          column
          fill-height
          align-center
          justify-center
          v-if="ReferralTotal === 0"
        >
          <img
            src="https://s3-eu-west-1.amazonaws.com/media.alternativebusinessfunding.co.uk/public/empty.png"
            alt=""
            height="191"
            width="600"
            style="opacity:0.4"
          />
          <p class="subtitle-1 mt-5 grey--text text--darken-2">
            No results found. Try changing the date range or removing some filters.
          </p>
        </v-layout>
        <v-layout class="calc-height" :class="{ isFiltered: 'filtered' }" column v-else-if="ReferralTotal > 0">
          <v-layout mt-3 mb-2>
            <v-flex xs6>
              <div class="title">{{ formattedTotal }} Result<span v-if="ReferralTotal !== 1">s</span></div>
            </v-flex>
            <v-flex xs6>
              <div v-if="ReferralCurrentPage > 1" class="pr-3" style="text-align: right">
                <a @click="scrollToPosition(0, true)">Back to top</a>
              </div>
            </v-flex>
          </v-layout>
          <v-flex
            class="calc-central-height"
            :class="{ isFiltered: 'filtered' }"
            ref="scrollContainer"
            @scroll="getScrollPosition"
          >
            <referral-card
              v-for="referral in ReferralPage"
              :key="referral._source.id"
              :referral="referral._source"
              :show-referrer="UserHasRole('Platform') || UserHasRole('Call Centre')"
              :show-owner="UserHasRole('Platform') || UserHasRole('Call Centre')"
            >
            </referral-card>
            <mugen-scroll
              :handler="scrollPage"
              scroll-container="scrollContainer"
              :threshold="0.7"
              :should-handle="!loading"
              v-if="pagesAvailable"
            >
              <v-layout align-center justify-center>
                <v-progress-circular
                  size="25"
                  :width="3"
                  color="accent"
                  indeterminate
                  class="py-4"
                ></v-progress-circular>
              </v-layout>
            </mugen-scroll>
          </v-flex>
        </v-layout>
        <v-layout class="calc-height" column v-else pr-4>
          <v-flex class="calc-central-height">
            <v-layout fill-height align-center justify-center>
              <v-progress-circular size="70" :width="7" color="accent" indeterminate></v-progress-circular>
            </v-layout>
          </v-flex>
        </v-layout>
      </v-container>

      <v-navigation-drawer
        :class="{ 'grey lighten-2': screenIsWideEnough, white: !screenIsWideEnough }"
        :clipped="screenIsWideEnough"
        :permanent="screenIsWideEnough"
        :temporary="!screenIsWideEnough"
        app
        fixed
        floating
        right
        v-model="showFilterDrawer"
        width="675"
      >
        <ReferralFilters @change="page = 1" @close="showFilterDrawer = false" />
      </v-navigation-drawer>

      <v-btn color="pink" dark fab fixed bottom right @click.stop="showNewCustomer = true" v-if="UserHasRole('Broker')">
        <v-icon>add</v-icon>
      </v-btn>

      <v-speed-dial
        v-model="fab"
        fixed
        bottom
        right
        open-on-hover
        transition="slide-y-transition"
        v-if="!UserHasRole('Call Centre') && !UserHasRole('Broker')"
      >
        <template v-slot:activator>
          <v-btn color="accent darken-1" dark fab v-model="fab">
            <v-icon v-if="fab">close</v-icon>
            <v-icon v-else>add</v-icon>
          </v-btn>
        </template>
        <v-tooltip left>
          <template v-slot:activator="{ on }">
            <v-btn v-on="on" fab dark small color="indigo" @click.stop="showReferralCreate = true">
              <v-icon>person_add</v-icon>
            </v-btn>
          </template>
          <span>Add Referral</span>
        </v-tooltip>
        <v-tooltip left>
          <template v-slot:activator="{ on }">
            <v-btn v-on="on" fab dark small color="red" @click.stop="showReferralUpload = true">
              <v-icon>group_add</v-icon>
            </v-btn>
          </template>
          <span>Upload CSV</span>
        </v-tooltip>
      </v-speed-dial>
    </v-content>

    <ReferralEditDialog :show.sync="showReferralCreate" />
    <ReferralUploadDialog :show.sync="showReferralUpload" />
    <customer-registration-dialog :customer="{}" :show.sync="showNewCustomer"></customer-registration-dialog>
  </div>
</template>

<script>
import _ from 'lodash';

import { mapGetters, mapActions } from 'vuex';
import ReferralCard from './ReferralCard';
import ReferralFilters from './ReferralFilters';
import ReferralKey from './Widgets/ReferralKey';
import MugenScroll from 'vue-mugen-scroll';
import ReferralEditDialog from './Widgets/ReferralEditDialog';
import ReferralUploadDialog from './Widgets/ReferralUploadDialog';
import AbfActiveFilters from '../../Abf/Filters/AbfActiveFilters';
import CustomerRegistrationDialog from '../Customers/CustomerRegistration/CustomerRegistrationDialog';

export default {
  components: {
    AbfActiveFilters,
    ReferralUploadDialog,
    ReferralEditDialog,
    ReferralCard,
    ReferralFilters,
    ReferralKey,
    MugenScroll,
    CustomerRegistrationDialog
  },
  name: 'Referrals',
  data: () => ({
    tabs: null,
    fab: false,
    showFilterDrawer: false,
    showReferralCreate: false,
    showReferralUpload: false,
    showNewCustomer: false
  }),
  computed: {
    ...mapGetters([
      'ReferralPageFilters',
      'EnquiryStatuses',
      'OwnersList',
      'PlatformUser',
      'ReferralCurrentPage',
      'ReferralDownloadingState',
      'ReferralLastPage',
      'ReferralPage',
      'ReferralPageScrollPosition',
      'ReferralSearch',
      'ReferralTotal',
      'UILeftDrawer',
      'UserHasRole'
    ]),
    isFiltered() {
      return (
        this.ReferralPageFilters.bool.toBeCalled ||
        this.ReferralPageFilters.noPhone ||
        this.ReferralPageFilters.withPhone ||
        this.ReferralPageFilters.eliteReferrer ||
        this.ReferralPageFilters.noEmail ||
        this.ReferralPageFilters.withEmail ||
        this.ReferralPageFilters.closed ||
        this.ReferralPageFilters.open ||
        this.ReferralPageFilters.referred ||
        this.ReferralPageFilters.selfReferred ||
        this.ReferralPageFilters.registered ||
        this.ReferralPageFilters.notRegistered ||
        this.ReferralPageFilters.questionsComplete ||
        this.ReferralPageFilters.questionsIncomplete ||
        this.ReferralPageFilters.autoSendEnquiries ||
        this.ReferralPageFilters.notAutoSendEnquiries ||
        this.ReferralPageFilters.noEnquiries ||
        this.ReferralPageFilters.referrerType ||
        this.ReferralPageFilters.referrer ||
        this.ReferralPageFilters.legalStatus ||
        this.ReferralPageFilters.funded ||
        this.ReferralPageFilters.owners.length > 0 ||
        this.ReferralPageFilters.enquiryStatuses.length > 0
      );
    },
    loading() {
      return this.ReferralTotal === null;
    },
    page: {
      get() {
        return this.ReferralCurrentPage;
      },
      set(page) {
        this.setReferralCurrentPage(page);
        this.getPage();
      }
    },
    search: {
      get() {
        return this.ReferralSearch;
      },
      set: _.debounce(function(value) {
        this.setReferralSearch(value).then(() => this.getPage());
      }, 350)
    },
    screenIsWideEnough: function() {
      return this.$vuetify.breakpoint.width > 1600;
    },
    pagesAvailable: function() {
      return this.ReferralCurrentPage < this.ReferralLastPage;
    },
    formattedTotal() {
      // return this.ReferralTotal >= 1000 ? '1000+' : this.ReferralTotal;
      return this.ReferralTotal;
    }
  },
  methods: {
    ...mapActions([
      'downloadReferrals',
      'getReferralsPage',
      'resetReferralFilters',
      'setReferralCurrentPage',
      'setReferralDownloadingState',
      'setReferralSearch',
      'setReferralScrollPosition',
      'setCustomerOrderBy',
      'setCustomerDirection',
      'setCustomerPageSize',
      'updateUILeftDrawerState',
      'getOwnersList'
    ]),
    download: function() {
      this.setReferralDownloadingState(true);
      this.downloadReferrals();
    },
    getPage: _.debounce(function() {
      this.getReferralsPage(false).catch(error => {
        console.log(error);
      });
    }, 250),
    getScrollPosition: _.debounce(function() {
      const scrollPosition =
        typeof this.$refs.scrollContainer !== 'undefined' ? this.$refs.scrollContainer.scrollTop : 0;
      this.setReferralScrollPosition(scrollPosition);
    }, 250),
    scrollPage() {
      if (this.ReferralCurrentPage < this.ReferralLastPage) {
        this.getReferralsPage(true).catch(error => {
          console.log(error);
        });
      }
    },
    scrollToPosition(top, smooth = true) {
      const behavior = smooth ? 'smooth' : 'auto';
      if ('scrollBehavior' in document.documentElement.style) {
        this.$refs.scrollContainer.scrollTo({ top, behavior });
      } else {
        this.$refs.scrollContainer.scrollTo(0, top);
      }
    }
  },
  mounted() {
    if (this.OwnersList.length <= 1) {
      this.getOwnersList();
    }
    if (this.ReferralPage.length === 0) {
      this.getReferralsPage();
    } else {
      this.scrollToPosition(this.ReferralPageScrollPosition, false);
    }
  }
  // beforeRouteEnter(to, from, next) {
  //   next(vm => {
  //     if (from.name === 'Dialler') {
  //       vm.resetReferralFilters();
  //     }
  //   });
  // }
};
</script>

<style lang="scss" scoped>
.calc-height {
  height: calc(100vh - 64px);
  max-height: calc(100vh - 64px);
  overflow: hidden;
  -webkit-overflow-scrolling: touch;
  &.filtered {
    height: calc(100vh - 180px);
    max-height: calc(100vh - 180px);
  }
}

.calc-central-height {
  height: calc(100vh - 72px);
  max-height: calc(100vh - 72px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  &.filtered {
    height: calc(100vh - 180px);
    max-height: calc(100vh - 180px);
  }
}

.selected {
  font-weight: 600;
}

::v-deep input[name='search'] {
  caret-color: black;
}
</style>
